/**
 * <bk-marquee> upgrades the static, wrapped category band into one or two scrolling rows.
 *
 * The server renders one accessible <ul data-marquee-list>. Each list item is a block wrapper
 * holding a `.marquee__link` whose data-row says which desktop row it belongs to. This module
 * groups the items into row wrappers, clones each row's track until it covers twice the band's
 * width (clones are aria-hidden and their links get tabindex -1: never read or tabbed to, yet still clickable, which inert would forbid) and
 * animates the rows with a CSS transform. Without JS, under prefers-reduced-motion, while a link
 * has keyboard focus (CSS :has) or while the band is off screen, the band is still.
 *
 * No string is written here: the pause button's name is rendered with `| t` and never changes;
 * its state is reported with aria-pressed.
 */
import { debounce, prefersReducedMotion } from '@theme/utils';

/**
 * matches() that answers false instead of throwing on a selector the browser does not know
 * (:focus-visible before Safari 15.4, :modal before Safari 15.6 / Chrome 105).
 * @param {Element | null} element
 * @param {string} selector
 */
function safeMatches(element, selector) {
  try {
    return Boolean(element?.matches(selector));
  } catch {
    return false;
  }
}

const DESKTOP_QUERY = '(min-width: 750px)';

class BkMarquee extends HTMLElement {
  /** @type {HTMLElement | null} */
  #nav = null;
  /** @type {HTMLElement[]} */
  #items = [];
  /** @type {HTMLElement[]} */
  #rows = [];
  /** @type {IntersectionObserver | null} */
  #intersection = null;
  /** @type {ResizeObserver | null} */
  #resize = null;
  /** @type {MediaQueryList | null} */
  #desktop = null;
  #built = false;
  #lastWidth = 0;
  #userPaused = false;
  #editorPaused = false;
  #onResize = debounce(() => this.#refill(), 200);

  connectedCallback() {
    this.#toggleButton?.addEventListener('click', this.#onToggle);

    if (this.#built) {
      this.#watch();
      return;
    }

    const source = this.querySelector('[data-marquee-list]');
    if (!(source instanceof HTMLElement)) return;

    this.#items = /** @type {HTMLElement[]} */ (
      [...source.children].filter((node) => node instanceof HTMLElement && node.querySelector('.marquee__link'))
    );
    if (this.#items.length === 0 || prefersReducedMotion()) return;

    this.#nav = source.parentElement;
    if (!this.#nav) return;

    source.remove();
    this.#built = true;
    this.#desktop = matchMedia(DESKTOP_QUERY);
    this.#desktop.addEventListener('change', this.#onBreakpoint);
    this.#build();
    this.#watch();
  }

  disconnectedCallback() {
    this.#toggleButton?.removeEventListener('click', this.#onToggle);
    this.#intersection?.disconnect();
    this.#resize?.disconnect();
    this.#intersection = null;
    this.#resize = null;
  }

  /** Pause while the theme editor has one of this band's blocks selected. */
  pauseForEditor() {
    this.#editorPaused = true;
    this.#reflectPaused();
  }

  resumeForEditor() {
    this.#editorPaused = false;
    this.#reflectPaused();
  }

  get #toggleButton() {
    return this.querySelector('[data-marquee-toggle]');
  }

  #onBreakpoint = () => {
    this.#build();
  };

  #onToggle = () => {
    this.#userPaused = !this.#userPaused;
    this.#reflectPaused();
  };

  #reflectPaused() {
    this.classList.toggle('marquee--paused', this.#userPaused || this.#editorPaused);
    this.#toggleButton?.setAttribute('aria-pressed', String(this.#userPaused));
  }

  /** (Re)group the authored items into rows for the current breakpoint, then fill them. */
  #build() {
    const nav = this.#nav;
    if (!nav || this.#items.length === 0) return;

    for (const row of this.#rows) row.remove();
    this.#rows = [];

    const oneRow = !this.#desktop?.matches && this.getAttribute('data-rows-mobile') !== '2';
    /** @type {Map<string, HTMLElement[]>} */
    const groups = new Map();
    for (const item of this.#items) {
      const row = item.querySelector('[data-row]')?.getAttribute('data-row') === '2' && !oneRow ? '2' : '1';
      const group = groups.get(row) ?? [];
      group.push(item);
      groups.set(row, group);
    }

    for (const key of ['1', '2']) {
      const groupItems = groups.get(key);
      if (!groupItems?.length) continue;

      const track = document.createElement('ul');
      track.className = 'marquee__track';
      track.setAttribute('role', 'list');
      track.append(...groupItems);

      const row = document.createElement('div');
      row.className = 'marquee__row';
      row.setAttribute('data-row', key);
      row.append(track);
      nav.append(row);
      this.#rows.push(row);
    }

    this.classList.add('marquee--animated');
    this.#lastWidth = 0;
    this.#refill();
    this.#reflectPaused();
  }

  /** Clone each row's track until the row is at least twice the band's width; set the loop distance. */
  #refill() {
    const width = this.clientWidth || window.innerWidth;
    if (width === this.#lastWidth) return;
    // Measuring while a focused link holds the static layout would record a wrapped width.
    if (safeMatches(document.activeElement, '.marquee__link:focus-visible') && this.contains(document.activeElement)) return;
    this.#lastWidth = width;

    for (const row of this.#rows) {
      const track = row.firstElementChild;
      if (!(track instanceof HTMLElement)) continue;
      for (const clone of row.querySelectorAll(':scope > [aria-hidden="true"]')) clone.remove();

      const unit = track.getBoundingClientRect().width;
      if (unit <= 0) continue;

      const copies = Math.max(1, Math.ceil((width * 2) / unit));
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < copies; index += 1) {
        const clone = /** @type {HTMLElement} */ (track.cloneNode(true));
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('role');
        // Copies must not repeat ids or the theme editor's block markers of the originals.
        for (const element of clone.querySelectorAll('*')) {
          element.removeAttribute('id');
          for (const name of element.getAttributeNames()) {
            if (name.startsWith('data-shopify')) element.removeAttribute(name);
          }
        }
        for (const link of clone.querySelectorAll('a')) link.setAttribute('tabindex', '-1');
        fragment.append(clone);
      }
      row.append(fragment);
      row.style.setProperty('--marquee-shift', `${unit}px`);
    }
  }

  #watch() {
    if (!this.#built) return;

    this.#intersection?.disconnect();
    this.#intersection = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.classList.toggle('marquee--offscreen', !entry.isIntersecting);
      },
      { rootMargin: '120px 0px' },
    );
    this.#intersection.observe(this);

    this.#resize?.disconnect();
    this.#resize = new ResizeObserver(this.#onResize);
    this.#resize.observe(this);
  }
}

if (!customElements.get('bk-marquee')) customElements.define('bk-marquee', BkMarquee);

// Theme editor: a selected block must stay readable, so its band stops while it is selected.
document.addEventListener('shopify:block:select', (event) => {
  const target = event.target;
  if (target instanceof Element) target.closest('bk-marquee')?.pauseForEditor?.();
});
document.addEventListener('shopify:block:deselect', (event) => {
  const target = event.target;
  if (target instanceof Element) target.closest('bk-marquee')?.resumeForEditor?.();
});
